import moment from 'moment-timezone';
import { dateIsDuringPlaceHolidayOrWeekEnd } from '../../../../../shared/utils/dateTime/dateValidations';
import {
  doctorIsOnIntensiveCareUnitSupportOnThisDay,
  hasIntensiveCareOnGuardCodeAndIsNotCanceled
} from '../../../../containers/Act/SaveAct/intensiveCareOptimizations/utils';
import { selectActivitiesInContext } from '../../../../containers/Activities/selectors';
import { getStore } from '../../../../reduxStore/configureStore';
import codesThatCannotBeBillWithEmergencyContextElement from '../../../../../shared/domain/activity/act/act-codes-that-cannot-be-billed-with-emergency-context-element';
import { Act as CoreAct } from 'shared/medyx-core/types/act';
import { ActivityStatus, ActivityType } from 'shared/medyx-core/types/activity';
import {
  getIntensiveCareGuardStartTimeRange,
  getIntensiveCareSupportStartTimeRange,
  isUserOnGuardDuty
} from 'shared/utils/intensive-care-period.utils';

const CLOSED_INTENSIVE_CARE_SUPPORT_CODES = ['9294', '9295', '42420', '42421', '42422', '42423'];

const OPEN_INTENSIVE_CARE_SUPPORT_CODES = [
  '53',
  '54',
  '8918',
  '8919',
  '9095',
  '9096',
  '15014',
  '15015',
  '16142',
  '16143'
];

export const isExceptionalEmergencyDuringOffWeekPeriod = (act: CoreAct): boolean => {
  if (!act.start || act.codes.length === 0) return false;

  return (
    !hasIntensiveCareOnGuardCodeAndIsNotCanceled(act) &&
    !isActMadeDuringNormalWorkingDay(act) &&
    !actHasCodesThatCantBeBilledWithEmergencyContextElement(act) &&
    !isActDuringIntensiveCarePeriod(act)
  );
};

const doctorsTodayActivities = (act: CoreAct) => {
  const activitiesInContext = selectActivitiesInContext()(getStore().getState()) || [];

  return activitiesInContext.filter((activityInContext) => activityInContext.date === act.date);
};

const isActMadeDuringNormalWorkingDay = (act: CoreAct): boolean => {
  const isDuringIntensiveCareSupport = doctorIsOnIntensiveCareUnitSupportOnThisDay(doctorsTodayActivities(act));

  if (isDuringIntensiveCareSupport) {
    return isDuringWorkingNormalTimeRange(act, isDuringIntensiveCareSupport);
  }

  return (
    !dateIsDuringPlaceHolidayOrWeekEnd(act.date, act.place) &&
    isDuringWorkingNormalTimeRange(act, isDuringIntensiveCareSupport)
  );
};

const isDuringWorkingNormalTimeRange = (act: CoreAct, isDuringIntensiveCareSupport: boolean): boolean => {
  const { place, start } = act;
  const billByAct = place?.billByAct || false;

  return moment(start).isBetween(
    moment(start).set({ hours: 7, minutes: 0, seconds: 0, milliseconds: 0 }),
    moment(start).set({
      hours: isDuringIntensiveCareSupport || billByAct ? 19 : 21,
      minutes: 0,
      seconds: 0,
      milliseconds: 0
    }),
    undefined,
    '[)'
  );
};

const isActDuringIntensiveCarePeriod = (currentAct: CoreAct) => {
  if (!currentAct.start) return false;
  const actsInContext: CoreAct[] = (selectActivitiesInContext()(getStore().getState()) || []).filter(
    (activity) => activity.type === ActivityType.ACT
  );

  const actsInSupportPeriod = actsInContext.filter((activity) => {
    if (!currentAct.start) return false;
    if (activity.status === ActivityStatus.CANCELED) return false;

    return moment(activity.start).isBetween(
      getIntensiveCareSupportStartTimeRange(currentAct.start).startTime,
      getIntensiveCareSupportStartTimeRange(currentAct.start).endTime,
      'milliseconds',
      '[)'
    );
  });
  const actsInGuardPeriod = actsInContext.filter((activity) => {
    if (!currentAct.start) return false;
    if (activity.status === ActivityStatus.CANCELED) return false;

    return moment(activity.start).isBetween(
      getIntensiveCareGuardStartTimeRange(currentAct.start).startTime,
      getIntensiveCareGuardStartTimeRange(currentAct.start).endTime,
      'milliseconds',
      '[)'
    );
  });

  const isUserOnClosedIntensiveCareSupport = actsInSupportPeriod.some((act) => {
    if (act.status === ActivityStatus.CANCELED) return false;

    return act.codes.some(({ code }) => CLOSED_INTENSIVE_CARE_SUPPORT_CODES.includes(code));
  });

  const isPatientOnOpenIntensiveCareSupport =
    currentAct.nam &&
    actsInSupportPeriod.some((act) => {
      if (act.nam !== currentAct.nam) return false;

      if (act.start && moment(act.start).isAfter(moment(currentAct.start))) return false;

      return act.codes.some(({ code }) => OPEN_INTENSIVE_CARE_SUPPORT_CODES.includes(code));
    });

  return (
    isUserOnGuardDuty(currentAct.start, actsInGuardPeriod) ||
    isUserOnClosedIntensiveCareSupport ||
    isPatientOnOpenIntensiveCareSupport
  );
};

const actHasCodesThatCantBeBilledWithEmergencyContextElement = ({ date, codes }: CoreAct): boolean => {
  const postAnnexe29ModificationsExcludedCodes = [
    '15950',
    '15951',
    '15952',
    '15953',
    '15954',
    '15955',
    '15956',
    '15957',
    '15958',
    '15959',
    '41000',
    '42420',
    '42421',
    '42422',
    '42423',
    '42424',
    '42425',
    '42426',
    '900',
    '9216',
    '9217'
  ];

  const annexe29ModificationsDate = moment('2026-03-23');

  return codes.some(
    ({ code }) =>
      codesThatCannotBeBillWithEmergencyContextElement.includes(code) ||
      (code.length === 5 && code.startsWith('19')) ||
      (code.length === 5 && code.startsWith('42')) ||
      (postAnnexe29ModificationsExcludedCodes.includes(code) && !moment(date).isBefore(annexe29ModificationsDate))
  );
};
